/**
 * One delegated tooltip for ALL SlickGrid cells — shown ONLY when a cell's content
 * is actually truncated (ellipsis/line-clamp overflow), styled to match the app's
 * MUI ToolTipComponent (cubeComponent/html/tooltip) exactly: same bubble colors,
 * typography, padding, arrow, top placement and zoom-in transition.
 *
 * Why delegated plain DOM (not the React ToolTipComponent itself): grid formatters
 * can't host React without per-cell createRoot, and `.slick-cell` is overflow:hidden
 * so an in-cell CSS tooltip would be clipped. A single set of document listeners
 * (~3 total, not per-cell) drives one floating element attached to <body>.
 * Cells expose their tooltip text via `data-cube-tip` (or fall back to textContent).
 */

// Keep in sync with ToolTipComponent (cubeComponent/html/tooltip/index.tsx).
const TOOLTIP_Z_INDEX = 13000;
const FONT_SIZE_PX = 14; // 0.875rem
const ARROW_W = 14; // MUI arrow: 1em of the tooltip font
const ARROW_H = 10; // MUI arrow: 0.71em
const ANCHOR_GAP = 14; // MUI placement margin (arrow lives inside this gap)
const ENTER_DELAY_MS = 100; // MUI Tooltip default enterDelay
const ENTER_MS = 225; // MUI Zoom in (theme.transitions.duration.enteringScreen)
const EXIT_MS = 195; // MUI Zoom out (theme.transitions.duration.leavingScreen)
const EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

let installed = false;
let tipEl: HTMLElement | null = null;
let textEl: HTMLElement | null = null;
let arrowEl: HTMLElement | null = null;
let arrowInnerEl: HTMLElement | null = null;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let currentCell: HTMLElement | null = null;
let visible = false;

/**
 * Convert a measured pixel length to a rem string against the LIVE root font-size.
 * The root font-size is a viewport-dependent clamp (`html { font-size: clamp(...) }`
 * in index.css), so a fixed `/16` would mis-place the tooltip on any viewport whose
 * html font-size ≠ 16px. Reading the computed root keeps placement pixel-exact.
 */
function toRem(px: number): string {
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return `${px / root}rem`;
}

function getTip(): HTMLElement {
  if (tipEl) return tipEl;

  tipEl = document.createElement("div");
  tipEl.setAttribute("role", "tooltip");
  Object.assign(tipEl.style, {
    position: "fixed",
    zIndex: String(TOOLTIP_Z_INDEX),
    maxWidth: "18.75rem",
    backgroundColor: "var(--primary-text)",
    color: "var(--surface-white)",
    fontSize: `${FONT_SIZE_PX / 16}rem`,
    fontWeight: "500",
    fontFamily: "inherit",
    lineHeight: "1.4",
    padding: "0.5rem 0.75rem",
    borderRadius: "0.25rem",
    wordWrap: "break-word",
    whiteSpace: "normal",
    pointerEvents: "none",
    opacity: "0",
    visibility: "hidden",
    transform: "scale(0)",
    transformOrigin: "center",
    transition: `transform ${ENTER_MS}ms ${EASING}, opacity ${ENTER_MS}ms ${EASING}`,
  } as Partial<CSSStyleDeclaration>);

  textEl = document.createElement("span");
  tipEl.appendChild(textEl);

  // MUI arrow: an overflow-hidden 1em x 0.71em box clipping a rotated square.
  arrowEl = document.createElement("span");
  Object.assign(arrowEl.style, {
    position: "absolute",
    width: `${ARROW_W / 16}rem`,
    height: `${ARROW_H / 16}rem`,
    overflow: "hidden",
    pointerEvents: "none",
  } as Partial<CSSStyleDeclaration>);
  arrowInnerEl = document.createElement("span");
  Object.assign(arrowInnerEl.style, {
    display: "block",
    width: `${ARROW_H / 16}rem`,
    height: `${ARROW_H / 16}rem`,
    margin: "0 auto",
    backgroundColor: "var(--primary-text)",
  } as Partial<CSSStyleDeclaration>);
  arrowEl.appendChild(arrowInnerEl);
  tipEl.appendChild(arrowEl);

  document.body.appendChild(tipEl);
  return tipEl;
}

function positionArrow(placement: "top" | "bottom", anchorCenterX: number, bubbleLeft: number, bubbleW: number): void {
  if (!arrowEl || !arrowInnerEl) return;
  const left = Math.min(Math.max(anchorCenterX - bubbleLeft - ARROW_W / 2, 6), bubbleW - ARROW_W - 6);
  arrowEl.style.left = toRem(left);
  if (placement === "top") {
    // Bubble above the cell → arrow on the bubble's bottom edge, pointing down.
    arrowEl.style.top = "";
    arrowEl.style.bottom = `${-ARROW_H / 16}rem`;
    arrowInnerEl.style.transform = "translateY(-50%) rotate(45deg)";
  } else {
    arrowEl.style.top = `${-ARROW_H / 16}rem`;
    arrowEl.style.bottom = "";
    arrowInnerEl.style.transform = "translateY(50%) rotate(45deg)";
  }
}

function cancelTimers(): void {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function hide(): void {
  cancelTimers();
  currentCell = null;
  if (!tipEl || !visible) return;
  visible = false;
  tipEl.style.transition = `transform ${EXIT_MS}ms ${EASING}, opacity ${EXIT_MS}ms ${EASING}`;
  tipEl.style.transform = "scale(0)";
  tipEl.style.opacity = "0";
  hideTimer = setTimeout(() => {
    if (tipEl && !visible) tipEl.style.visibility = "hidden";
  }, EXIT_MS);
}

function show(text: string, anchor: DOMRect): void {
  const el = getTip();
  if (!textEl) return;
  textEl.textContent = text;

  // Render invisibly, measure, then place and zoom in (matches MUI top placement + Zoom).
  el.style.transition = "none";
  el.style.transform = "scale(0)";
  el.style.opacity = "0";
  el.style.visibility = "hidden";
  el.style.left = "0px";
  el.style.top = "0px";
  requestAnimationFrame(() => {
    if (!tipEl || !currentCell) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const anchorCenterX = anchor.left + anchor.width / 2;
    const left = Math.max(4, Math.min(anchorCenterX - w / 2, window.innerWidth - w - 4));

    let placement: "top" | "bottom" = "top";
    let top = anchor.top - h - ANCHOR_GAP;
    if (top < 4) {
      placement = "bottom";
      top = anchor.bottom + ANCHOR_GAP;
    }

    positionArrow(placement, anchorCenterX, left, w);
    el.style.left = toRem(left);
    el.style.top = toRem(top);
    el.style.visibility = "visible";
    // Next frame so the scale(0) start state is committed before transitioning.
    requestAnimationFrame(() => {
      if (!tipEl || !currentCell) return;
      visible = true;
      el.style.transition = `transform ${ENTER_MS}ms ${EASING}, opacity ${ENTER_MS}ms ${EASING}`;
      el.style.transform = "scale(1)";
      el.style.opacity = "1";
    });
  });
}

/**
 * Strip native `title` attributes on the hovered chain (they would show the unstyled OS
 * tooltip ON TOP of ours — legacy formatters and library internals still set them).
 * Returns the first stripped title so its text is not lost: we show it ourselves.
 */
function absorbNativeTitle(start: HTMLElement, cell: HTMLElement): { text: string; el: HTMLElement } | null {
  let node: HTMLElement | null = start;
  let absorbed: { text: string; el: HTMLElement } | null = null;
  while (node) {
    const title = node.getAttribute?.("title");
    if (title) {
      node.removeAttribute("title");
      if (!absorbed) absorbed = { text: title, el: node };
    }
    if (node === cell) break;
    node = node.parentElement;
  }
  return absorbed;
}

/**
 * Nearest declared tooltip text walking `start` -> cell (inclusive). Formatters put `data-cube-tip`
 * on whichever element OWNS the text, which is not always the element that ends up clipping: in a
 * user-tag row the chip label ellipsizes inside a chip inside the row, and only the row/chip knows
 * the full comma-separated list. Reading the clipped node alone would miss it and fall through to
 * textContent (avatar initials mashed into the names).
 */
function closestTip(start: HTMLElement | null, cell: HTMLElement): string {
  let node: HTMLElement | null = start;
  while (node) {
    const tip = node.getAttribute?.("data-cube-tip");
    if (tip) return tip;
    if (node === cell) break;
    node = node.parentElement;
  }
  return "";
}

function onOver(e: Event): void {
  const start = e.target as HTMLElement | null;
  const cell = start?.closest?.(".slick-cell") as HTMLElement | null;
  if (!cell || !start) return;
  const absorbed = absorbNativeTitle(start, cell);
  // Moving between children of the same cell — the tooltip (or pending show) stands.
  if (cell === currentCell) return;

  // Walk hovered node -> cell; the element that actually clips is the tooltip trigger.
  let node: HTMLElement | null = start;
  let clipEl: HTMLElement | null = null;
  while (node && node !== cell.parentElement) {
    if (node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1) {
      clipEl = node;
      break;
    }
    if (node === cell) break;
    node = node.parentElement;
  }
  // A title that says more than the visible text was an intentional, always-on tooltip —
  // keep showing it (styled) even without truncation. Otherwise: no ellipsis, no tooltip.
  const informativeTitle =
    absorbed && absorbed.text.trim() && absorbed.text.trim() !== (absorbed.el.textContent ?? "").trim()
      ? absorbed.text.trim()
      : "";
  if (!clipEl && !informativeTitle) {
    hide();
    return;
  }
  const text = (
    closestTip(clipEl, cell) ||
    cell.getAttribute("data-cube-tip") ||
    // The clipping box can sit ABOVE every tipped element (the cell itself, or a wrapper the
    // formatter does not own) — then the walk above finds nothing because the tips are BELOW it.
    // The formatter's root node is the one element that speaks for the whole cell, so ask it before
    // resorting to raw text (which concatenates decorative content: avatar initials + names).
    cell.firstElementChild?.getAttribute("data-cube-tip") ||
    informativeTitle ||
    absorbed?.text ||
    clipEl?.textContent ||
    cell.textContent ||
    ""
  ).trim();
  if (!text) {
    hide();
    return;
  }
  cancelTimers();
  currentCell = cell;
  showTimer = setTimeout(() => {
    if (currentCell === cell && document.contains(cell)) show(text, cell.getBoundingClientRect());
  }, ENTER_DELAY_MS);
}

function onOut(e: Event): void {
  const from = (e.target as HTMLElement)?.closest?.(".slick-cell") as HTMLElement | null;
  if (!from) return;
  const to = (e as MouseEvent).relatedTarget as HTMLElement | null;
  // Only hide when the pointer truly leaves the cell (not on child-to-child moves).
  if (!to || to.closest?.(".slick-cell") !== from) hide();
}

// Keyed on window (not module scope): on Vite HMR the module reloads as a NEW instance
// while the old one's document listeners survive — without tearing the old instance down,
// every hot update stacks another live tooltip (seen as double tooltips in dev).
const GLOBAL_CLEANUP_KEY = "__cubeGridCellTooltipDispose__";

/** Install once (idempotent, hot-reload safe). Self-invoked on import. */
export function installGridCellTooltip(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;

  const w = window as unknown as Record<string, unknown>;
  const disposePrevious = w[GLOBAL_CLEANUP_KEY] as (() => void) | undefined;
  disposePrevious?.();

  document.addEventListener("mouseover", onOver, true);
  document.addEventListener("mouseout", onOut, true);
  document.addEventListener("mousedown", hide, true);
  window.addEventListener("scroll", hide, true);

  w[GLOBAL_CLEANUP_KEY] = () => {
    cancelTimers();
    document.removeEventListener("mouseover", onOver, true);
    document.removeEventListener("mouseout", onOut, true);
    document.removeEventListener("mousedown", hide, true);
    window.removeEventListener("scroll", hide, true);
    tipEl?.remove();
    tipEl = null;
    textEl = null;
    arrowEl = null;
    arrowInnerEl = null;
    currentCell = null;
    visible = false;
  };
}

installGridCellTooltip();
